import {
  CallbackHelper,
  RequestHelper,
  CallbackType,
} from '../../../custom_modules/textcontrol-promises-develop/src/helper/module.js';
import { Injectable, OnDestroy, OnInit, Signal, WritableSignal, forwardRef, inject, signal } from '@angular/core';
import {
  Observable,
  bindCallback,
  combineLatest,
  concatMap,
  filter,
  forkJoin,
  fromEvent,
  map,
  mergeMap,
  of,
  startWith,
  tap,
  toArray,
  BehaviorSubject,
  Subscription,
  from,
  take,
  switchMap,
} from 'rxjs';
import {
  EmptyCallback,
  FindOptions,
  FindResult,
  FormField,
  InputPositionChangedEvent,
  PageInformation,
  Point,
  SelectionPosition,
  textViewLocationChangedEvent,
  ZoomFactorChangedEventArgs,
} from './text-control.service.interface';
import { eventsToTrack } from '../components/edit-document/edit-document.helpers';
import { AppInsightsService } from './app-insights.service';
import { WindowService } from './window.service';
import { DocumentDataService } from './document-data.service';
import { DocumentService } from './document.service';
import { TimerService } from './timer.service';
import { TextControlFormFieldsService } from './text-control-formfields.service';
import { Form } from '@angular/forms';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { HelperService } from './helper.service';
import { TXdocsSearchScope } from '../models/find-and-replace.model';
import { KeyValue } from '@angular/common';
import { ObjectModel } from '../models/object.model';
import { cloneDeep, has, reject } from 'lodash';
import { FormField as ffPromise, FormFieldCollection, TextControlContext } from 'textcontrol-promises-develop';
import { id } from 'date-fns/locale';
import { TextChar } from 'custom_modules/textcontrol-promises-develop/src/TextChar.js';
import { LetterheadModel, LetterheadType } from '../models/letterhead.model';
import { recalcImageRatioFromTwips } from 'src/app/helpers/position-helpers';
import { DictionaryService } from './dictionary.service';

declare const TXTextControl: any;
declare const document: any;
@Injectable()
export class TextControlService implements OnInit, OnDestroy {
  private headerFooter: any;
  private subscriptions = new Subscription();
  private _txTextSessionID: string = '';
  public txTextSettings: {} | undefined;
  private isInActiveFind: boolean = false;
  private replaceAllCalled: boolean = false;
  get txTextSessionID(): string {
    return this._txTextSessionID;
  }

  public lastTimeDocumentChanged: number = 0;
  public position$: Observable<{ zoom: number; location: Point }>;
  private _replaceCalled$: BehaviorSubject<FormField | undefined> = new BehaviorSubject<FormField | undefined>(
    undefined
  );

  private _zoomEvent$: Observable<ZoomFactorChangedEventArgs> = of({ zoomFactor: 100 });
  public _zoom$: BehaviorSubject<number> = new BehaviorSubject<number>(100);
  get zoom$(): Observable<number> {
    return this._zoom$.asObservable();
  }

  private _locationEvent$: Observable<textViewLocationChangedEvent> = of({ location: { x: 0, y: 0 } });
  public _location$: BehaviorSubject<Point> = new BehaviorSubject<Point>({ x: 0, y: 0 });
  get location$(): Observable<Point> {
    return this._location$;
  }

  get activeField$(): Observable<FormField | undefined> {
    return this.formFieldsService.activeField$;
  }

  private formFieldToDeleteFromEditDocumentComponent: string = '';
  public setFormFieldToDelete(name: any) {
    this.formFieldToDeleteFromEditDocumentComponent = name;
  }

  get replaceCalled$(): Observable<FormField | undefined> {
    return this._replaceCalled$.asObservable();
  }

  public textSelectLength = 0;
  public textSelectStart = 0;
  private underscoreAnswers: any;
  private underscoreAnswersKey: string = 'UnderscoreAnswers';
  private emptyFieldPlaceholder: string = '*_______________*';
  private _isFormFieldFocused: boolean = false;
  private _isPreFormattingForPrint: boolean = false;
  public formFieldsService = inject(TextControlFormFieldsService); //circular dependency
  public clipboardDataTransferInProgress: WritableSignal<boolean> = signal(false);
  public clipboardDataTransferProgressPercentage = signal(0);
  public clipboardDataTransferSelectionPosition: WritableSignal<SelectionPosition> = signal({
    start: 0,
    length: 0,
  } as SelectionPosition);
  public currentSelection: WritableSignal<SelectionPosition> = signal({
    start: 0,
    length: 0,
  });
  public currentTextPartLength: WritableSignal<number> = signal(0);

  public get isFormFieldFocused() {
    return this._isFormFieldFocused;
  }

  public set isFormFieldFocused(newVal: boolean) {
    this._isFormFieldFocused = newVal;
  }

  constructor(
    private logger: AppInsightsService,
    private windowService: WindowService,
    private documentDataService: DocumentDataService,
    private documentService: DocumentService,
    private timerService: TimerService,
    private helperService: HelperService,
    private dictionaryService: DictionaryService
  ) {
    this.position$ = combineLatest([this.zoom$, this.location$]).pipe(
      map(([zoom, location]: [any, any]) => ({
        // this allows easy access to the values by name
        zoom,
        location,
      }))
    );
  }

  ngOnInit() {}

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  public async loadHeaderFooter() {
    var tpCount = await this.formFieldsService.getTextPartsCount();
    for (let i = 0; i < tpCount; i++) {
      const tp = await this.formFieldsService.getTextPartAt(i);
      if (tp.textPartType === 'headerFooter') {
        this.headerFooter = tp;
      }
    }
  }

  public async checkDocumentForAutoSave(): Promise<boolean> {
    return new Promise((resolve) => {
      this.documentService.document$.pipe(take(1)).subscribe((doc) => {
        //verify if this is a new document - autosave all that have a valid dcmID
        if (doc && doc.dcmID) {
          resolve(true);
        } else {
          TXTextControl.paragraphs.getCount((count: number) => {
            if (count > 1) {
              resolve(true);
            } else {
              TXTextControl.paragraphs.getItemAtLocation(1, 1, (item: any) => {
                item.getText((text: string) => {
                  if (text.length > 20) {
                    resolve(true);
                  } else {
                    resolve(false);
                  }
                });
              });
            }
          });
        }
      });
    });
  }

  TxDocumentEditorLoaded(ConnectionID: string | null) {
    if (ConnectionID != null) {
      this._txTextSessionID = ConnectionID;
      console.log('TxText connection:', ConnectionID, this.txTextSessionID);
      console.log(
        `---> TxText connection established. Checking SessionId : ${this.windowService.getSessionIdFromWindow()}`
      );
    } else {
      this.logger.logException(new Error('TXText failed to establish connection'), SeverityLevel.Error, {
        ConnectionID: ConnectionID,
        TXSettings: this.txTextSettings,
      });
    }
  }

  listenToEventsAndLog() {
    eventsToTrack.forEach((eventName) => {
      TXTextControl.addEventListener(eventName, (event: any) => {
        if (event['textField'] != undefined) {
          this.logger.logTrace('TXTextEvent', { eventName: eventName, name: event.textField.name });
        } else {
          this.logger.logTrace('TXTextEvent', { eventName: eventName, event: event });
        }
      });
    });
  }

  /// <summary>
  /// This fires everytime the cursor position changes.
  /// it returns https://docs.textcontrol.com/textcontrol/asp-dotnet/ref.javascript.inputpositionchangedeventargs.object.htm
  /// Column, line, page, section, textposition
  /// </summary>
  public watchInputPositionChangedObservable(): Observable<InputPositionChangedEvent> {
    return fromEvent(TXTextControl, 'inputPositionChanged', { capture: false });
  }

  public watchDocumentLoadedObservable(): Observable<EmptyCallback> {
    return fromEvent<EmptyCallback>(TXTextControl, 'documentLoaded', { capture: false }).pipe(
      tap((e) => {
        console.log('documentLoaded', e);
      })
    );
  }

  public watchTextViewLocationChanged(): Observable<textViewLocationChangedEvent> {
    return fromEvent(TXTextControl, 'textViewLocationChanged', { capture: false });
  }

  public watchSelectionPositionObservable(): Observable<SelectionPosition> {
    return this.getSelectionInformation();
  }
  public watchChangedObservable(): Observable<EmptyCallback> {
    return fromEvent<EmptyCallback>(TXTextControl, 'changed', { capture: false }).pipe(
      filter((e) => this._isPreFormattingForPrint === false),
      tap((e) => {
        this.lastTimeDocumentChanged = new Date().getTime();
      })
    );
  }
  public watchDocumentAndSelectionChangeObservable(): Observable<{
    selection: SelectionPosition;
    position: InputPositionChangedEvent;
  }> {
    let savedTextPosition = 0;
    let savedSelectionStart = 0;
    let savedSelectionLength = 0;
    let savedLastDocumentChange = 0;
    return combineLatest([
      this.watchInputPositionChangedObservable().pipe(
        tap(() => {
          this.getTextPartLengthAsPromise();
        }),
        startWith({ column: 0, line: 0, page: 0, section: 0, textPosition: 0 }),
        concatMap((inputPos) =>
          this.watchSelectionPositionObservable().pipe(
            tap((sel) => this.currentSelection.set(sel)),
            map((sel) => ({ sel, inputPos }))
          )
        )
      ),
      this.watchChangedObservable().pipe(startWith({})),
    ]).pipe(
      map(([selection, empty]) => {
        return { selection: selection.sel, position: selection.inputPos };
      }),
      filter(
        (pos) =>
          savedTextPosition !== pos.position.textPosition ||
          savedSelectionStart !== pos.selection.start ||
          savedSelectionLength !== pos.selection.length ||
          savedLastDocumentChange !== this.lastTimeDocumentChanged
      ),
      tap((pos) => {
        savedTextPosition = pos.position.textPosition;
        savedSelectionStart = pos.selection.start;
        savedSelectionLength = pos.selection.length;
        savedLastDocumentChange = this.lastTimeDocumentChanged;
      })
    );
  }
  private watchFieldArrayLocationsActiveFieldObservable(): Observable<{
    selectionPos: { selection: SelectionPosition; position: InputPositionChangedEvent };
    selectedField: FormField | undefined;
  }> {
    return this.watchDocumentAndSelectionChangeObservable().pipe(
      // //Cross join the actions with the form fields
      mergeMap((selectedPosition) =>
        this.formFieldsService.getCurrentFormFieldObservable().pipe(
          map((selectedField) => ({
            selectionPos: selectedPosition,
            selectedField: selectedField,
          }))
        )
      )
    );
  }

  public watchTextFieldDeletedObservable(): Observable<any> {
    return fromEvent(TXTextControl, 'textFieldDeleted', { capture: false });
  }

  watchClipboardDataTransferStartObservable(): Observable<any> {
    return fromEvent(TXTextControl, 'clipboardDataTransferStart', { capture: false }).pipe(
      tap(() => {
        this.clipboardDataTransferInProgress.set(true);
        this.clipboardDataTransferProgressPercentage.set(0);
      }),
      switchMap(() => this.getSelectionInformation()),
      tap((selection: SelectionPosition) => this.clipboardDataTransferSelectionPosition.set(selection))
    );
  }

  watchClipboardDataTransferProgressObservable(): Observable<any> {
    return fromEvent(TXTextControl, 'clipboardDataTransferProgress', { capture: false }).pipe(
      tap((progress: any) => {
        this.clipboardDataTransferProgressPercentage.set(progress.progress);
      })
    );
  }

  watchClipboardDataTransferCompleteObservable(): Observable<any> {
    return fromEvent(TXTextControl, 'clipboardDataTransferComplete', { capture: false }).pipe(
      tap(() => {
        this.clipboardDataTransferInProgress.set(false);
        this.clipboardDataTransferProgressPercentage.set(100);
      })
    );
  }

  watchClipboardDataTransferAbortedObservable(): Observable<any> {
    return fromEvent(TXTextControl, 'clipboardDataTransferAborted', { capture: false }).pipe(
      tap(() => {
        this.clipboardDataTransferInProgress.set(false);
        this.clipboardDataTransferProgressPercentage.set(100);
      })
    );
  }

  public forceUpdateSelectedField(): Observable<FormField | undefined> {
    this.timerService.start();
    // forces a get of the current field which updates formfielservice.currentSelectedField
    return from(this.formFieldsService.getCurrentFormFieldPromise()).pipe(
      tap((selectedField: FormField | undefined) => {
        if (selectedField && selectedField.details) {
          this.timerService.stop('updateSelectedField');
        }
      })
    );
  }

  private getSelectionInformation(): Observable<SelectionPosition> {
    //this.timerService.start();
    return forkJoin({
      start: bindCallback<readonly number[], readonly number[]>(TXTextControl.selection.getStart).bind(
        TXTextControl.selection
      )(),
      length: bindCallback<readonly number[], readonly number[]>(TXTextControl.selection.getLength).bind(
        TXTextControl.selection
      )(),
    }).pipe(
      map(({ start, length }) => {
        //this.timerService.stop('getSelectionInformation');
        return { start: start[0] ?? start, length: length[0] ?? length };
      })
    );
  }

  public getSelectionInformationPromise(): Promise<SelectionPosition> {
    return Promise.all([
      new Promise<number>((resolve) => TXTextControl.selection.getStart((start: number) => resolve(start))),
      new Promise<number>((resolve) => TXTextControl.selection.getLength((length: number) => resolve(length))),
    ]).then(([start, length]) => ({ start, length }));
  }

  //Used for commented out code.
  // will remove when we are sure we don't need it.
  public setFormFieldValueObs(name: string, value: string) {
    this.timerService.start();
    return from(this.formFieldsService.allFields).pipe(
      filter((ff) => {
        return ff.details.name === name;
      }),
      map((ff: FormField) => {
        this.timerService.stop('setFormFieldsObservable');
        return from(this.formFieldsService.setFieldTextPromise(ff, value));
      }),
      toArray()
    );
  }

  public saveDocumentAsWord(): Promise<string> {
    return new Promise((resolve) => {
      TXTextControl.saveDocument(TXTextControl.streamType.MSWord, (e: any) => {
        resolve(e.data);
      });
    });
  }

  public saveDocument(): Promise<string> {
    return new Promise((resolve) => {
      TXTextControl.saveDocument(TXTextControl.streamType.InternalUnicodeFormat, (e: any) => {
        resolve(e.data);
      });
    });
  }

  public loadDocument(document: string) {
    this.timerService.start();
    return new Promise((resolve) => {
      TXTextControl.loadDocument(TXTextControl.streamType.InternalUnicodeFormat, document, (e: any) => {
        if (e) {
          this.timerService.stop('loadDocument');
          resolve(e.data);
        }
      });
    });
  }

  public closeDocument() {
    return new Promise((resolve) => {
      TXTextControl.loadDocument(TXTextControl.streamType.PlainText, '', () => resolve(true));
    });
  }

  /**
   * @deprecated This function is deprecated. This itterates over all fields.
   */
  public setFormFieldValue(name: string, value: string) {
    this.timerService.start();
    TXTextControl.textParts.foreach((tp: any) => {
      tp.formFields.forEach((i: any) => {
        i.getName((n: string) => {
          // if (name === n) {
          // change made for form fields that include dim index
          if (n.includes(name)) {
            if (i.constructor.name === 'CheckFormField') {
              i.setChecked(value.toLowerCase() === 'true');
            } else if (i.constructor.name === 'DateFormField') {
              i.setDate(new Date(value).getTime());
              i.setText(value);
            } else {
              i.setText(value);
            }
          }
        });
      });
    });
    this.timerService.stop('setFormFieldValue');
  }

  public enableSpellCheck(): Promise<boolean> {
    return new Promise((resolve) => {
      TXTextControl.setIsSpellCheckingEnabled(
        true,
        () => {
          resolve(true);
        },
        (error: any) => {
          resolve(false);
        }
      );
    });
  }

  public setLocalClipboard() {
    TXTextControl.clipboardMode = TXTextControl.ClipboardMode.Client;
  }

  public navigateToNextFormField(direction: number, skipAnswered: boolean): Promise<boolean> {
    console.log(`---> Navigate To Next Form Field is called with step: ${direction} and skipAnswered: ${skipAnswered}`);

    return new Promise((resolve) => {
      TXTextControl.formFields.getItem((current: any) => {
        if (current) {
          if (direction > 0) {
            this.setCursorForTheNextFieldWhenCursorIsInsideAField(current, skipAnswered);
          } else {
            this.setCursorForThePreviousFieldWhenCursorIsInsideAField(current, skipAnswered);
          }
        } else {
          //TODO:: this implementation uses txtext control navigation....
          //refactor as needed after webinar to account for skipping answered fields AND skipping calculated fields
          if (direction > 0) {
            this.setCursorForTheNextFieldWhenCursorIsNotInsideAField();
          } else {
            this.setCursorForThePreviousFieldWhenCursorIsNotInsideAField();
          }
        }
      }, resolve(true));
    });
  }

  private setCursorForTheNextFieldWhenCursorIsInsideAField(currentObj: any, skipAnswered: boolean = false) {
    currentObj.getID((id: number) => {
      const idx = this.formFieldsService.allFields.findIndex((e) => e.details.id === id);
      if (idx === -1 || idx + 1 > this.formFieldsService.allFields.length) {
        return this.navigateToFormField(1);
      }

      for (let i = idx + 1; i < this.formFieldsService.allFields.length; i++) {
        if (this.checkIfNavigatableField(i, skipAnswered)) {
          return this.navigateToFormField(this.formFieldsService.allFields[i].details.id);
        }
      }
      //If there is no next field then navigate to first field
      return this.navigateToFirstEditableFormField(skipAnswered);
    });
  }

  private setCursorForThePreviousFieldWhenCursorIsInsideAField(currentObj: any, skipAnswered: boolean = false) {
    currentObj.getID((id: number) => {
      const idx = this.formFieldsService.allFields.findIndex((e) => e.details.id === id);
      if (idx - 1 < 0) {
        return;
      }

      for (let i = idx - 1; i >= 0; i--) {
        if (this.checkIfNavigatableField(i, skipAnswered)) {
          return this.navigateToFormField(this.formFieldsService.allFields[i].details.id);
        }
      }
      return Promise.resolve(true);
    });
  }

  private checkIfNavigatableField(index: number, skipAnswered: boolean = false): boolean {
    if (this.formFieldsService.allFields.length <= 0) {
      return false;
    }

    var obj = this.documentDataService.getObject(this.formFieldsService.allFields[index].details.name);
    if (obj && obj.objType === 'Q') {
      return this.checkIfSkipAnsweredAndTextEmpty(skipAnswered, this.formFieldsService.allFields[index].details.text);
    }
    if (obj && obj.objType === 'I' && obj.qToken) {
      var qTokenObj = this.documentDataService.getObject(obj.qToken);
      if (qTokenObj && (qTokenObj.qType === 'T' || qTokenObj.qType === 'E' || qTokenObj.qType === 'C')) {
        return this.checkIfSkipAnsweredAndTextEmpty(skipAnswered, this.formFieldsService.allFields[index].details.text);
      }
    }
    return false;
  }

  private checkIfSkipAnsweredAndTextEmpty(skipAnswered: boolean, text: string): boolean {
    if (!skipAnswered) {
      return true;
    } else if (skipAnswered && text === '') {
      return true;
    }
    return false;
  }

  private setCursorForTheNextFieldWhenCursorIsNotInsideAField() {
    document.getElementById('ribbonTabFormFields_btnNextFormField').click();
  }

  private setCursorForThePreviousFieldWhenCursorIsNotInsideAField() {
    document.getElementById('ribbonTabFormFields_btnPreviousFormField').click();
  }

  public navigateToFirstEditableFormField(skipAnswered: boolean = false) {
    if (this.formFieldsService.allFields.length <= 0) {
      return;
    }
    const fieldPositions = this.formFieldsService.allFields
      .filter((x) => x.details.id !== 0 && this.checkIfSkipAnsweredAndTextEmpty(skipAnswered, x.details.text))
      .map((x) => x.details.id);

    this.navigateToFormField(fieldPositions[0]);
  }

  public navigateToFormField(id: number): Promise<boolean> {
    return new Promise((resolve) => {
      TXTextControl.formFields.getItem(
        (ff: any) => {
          if (ff) {
            ff.scrollTo();
            resolve(true);
          } else {
            resolve(false);
          }
        },
        (error: any) => {
          resolve(false);
        },
        id
      );
    });
  }

  public async searchFormFields(answerSearch: string) {
    if (!answerSearch) {
      return;
    }
    var activeField = this.formFieldsService.activeSelectedField;

    var item = this.formFieldsService.allFields.find((ff) =>
      ff.details.text.toLowerCase().includes(answerSearch.toLowerCase())
    );
    //Id is ordered and used in navigation methods to be able to track the next field.
    if (activeField) {
      item = this.formFieldsService.allFields.find(
        (ff) =>
          ff.details.text.toLowerCase().includes(answerSearch.toLowerCase()) && ff.details.id > activeField!.details.id
      );
      //If the active field is end of the document, we want it to bounce back to the first field found.
      if (!item) {
        var item = this.formFieldsService.allFields.find((ff) =>
          ff.details.text.toLowerCase().includes(answerSearch.toLowerCase())
        );
      }
    }
    if (item) {
      await this.navigateToFormField(item.details.id);
      this.focus();
    }
  }

  public async countUnansweredFormFields(): Promise<number> {
    return new Promise((resolve) => {
      TXTextControl.formFields.getCount(async (c: number) => {
        let unansweredCount: number = 0;
        for (let i = 0; i < c; i++) {
          const unanswered = await this.checkIfFormFieldUnanswered(i);
          if (unanswered) {
            unansweredCount += 1;
          }
        }
        resolve(unansweredCount);
      });
    });
  }

  private checkIfFormFieldUnanswered(i: number): Promise<boolean> {
    return new Promise((resolve) => {
      TXTextControl.formFields.elementAt(i, (ff: any) => {
        ff.getText((value: string) => {
          if (value == null || value === '') {
            resolve(true);
          } else {
            resolve(false);
          }
        });
      });
    });
  }

  public setBold() {
    TXTextControl.selection.getBold((isBold: boolean) => {
      TXTextControl.selection.setBold(!isBold);
    });
  }

  public async setBoldAsync(bold: boolean): Promise<void> {
    return new Promise((resolve) => {
      TXTextControl.selection.setBold(bold, (ff: any) => {
        resolve();
      });
    });
  }
  public async setItalicAsync(italic: boolean): Promise<void> {
    return new Promise((resolve) => {
      TXTextControl.selection.setItalic(italic, (ff: any) => {
        resolve();
      });
    });
  }

  public async setUnderlineAsync(underlinestyle: number): Promise<void> {
    return new Promise((resolve) => {
      TXTextControl.selection.setUnderline(underlinestyle, (ff: any) => {
        resolve();
      });
    });
  }

  public setItalic() {
    TXTextControl.selection.getItalic((isItalic: boolean) => {
      TXTextControl.selection.setItalic(!isItalic);
    });
  }

  public setUnderline() {
    TXTextControl.selection.getUnderline((underline: any) => {
      if (underline === TXTextControl.FontUnderlineStyle.None) {
        TXTextControl.selection.setUnderline(TXTextControl.FontUnderlineStyle.Single);
      } else {
        TXTextControl.selection.setUnderline(TXTextControl.FontUnderlineStyle.None);
      }
    });
  }

  public addIndent() {
    TXTextControl.selection.increaseIndent();
  }

  public decreaseIndent() {
    //This is a bug in TXTextControl. It does not decrease the indent but it increases it.
    TXTextControl.selection.decreaseIndent();
  }

  showFieldNavigatorSideBar(show: boolean) {
    TXTextControl.showSideBar(TXTextControl.SideBarType.FieldNavigator, show);
  }

  registerEventListener(eventName: string, callback: any) {
    TXTextControl.addEventListener(eventName, callback);
  }
  removeEventListener(eventName: string, callback: any) {
    TXTextControl.removeEventListener(eventName, callback);
  }

  removeFromDOM() {
    TXTextControl.removeFromDom();
  }

  selectRibbonTab(tabId: string) {
    TXTextControl.ribbon.selectedTab = tabId;
  }

  async printDocument() {
    const savedTextPosition = await this.getInputPosition();
    await this.setInputPosition(0, 0, false);
    await this.callAsPromise(TXTextControl.beginUndoAction, 'ReplaceEmptyValues');
    const emptyFields = await this.replaceEmptyFieldValues(this.formFieldsService.allFields);
    TXTextControl.printDocument();
    document.addEventListener(
      'printResultReceived',
      async () => {
        // setting a timeout to allow the print dialog to open
        setTimeout(async () => {
          await this.callAsPromiseNoParams(TXTextControl.endUndoAction);
          await this.revertReplacingEmptyValues(emptyFields);
          await this.setInputPosition(savedTextPosition, 0, true);
          this.focus();
        }, 1000);
      },
      { capture: false, once: true, passive: true }
    );
  }

  public replaceText(result: FormField) {
    this._replaceCalled$.next(result);
  }

  public isFieldInRange(rangeStart: number, rangeLength: number): boolean {
    return this.formFieldsService.allFields.some((field) => {
      // Ensure the field has positioning and both start and length are defined
      if (field.positioning && field.positioning.start !== undefined && field.positioning.length !== undefined) {
        const fieldStart = field.positioning.start;
        const fieldEnd = fieldStart + field.positioning.length;

        const rangeEnd = rangeStart + rangeLength;

        // Check if the field falls within the specified range
        return fieldStart < rangeEnd && fieldEnd > rangeStart;
      }
      return false; // if positioning, start, or length is not defined
    });
  }

  public getFieldsInRange(rangeStart: number, rangeLength: number): FormField[] {
    return this.formFieldsService.allFields.filter((field) => {
      // Ensure the field has positioning and both start and length are defined
      if (field.positioning && field.positioning.start !== undefined && field.positioning.length !== undefined) {
        const fieldStart = field.positioning.start;
        const fieldEnd = fieldStart + field.positioning.length;

        const rangeEnd = rangeStart + rangeLength;

        // Check if the field falls within the specified range
        return (
          (rangeStart <= fieldStart && rangeEnd >= fieldStart && rangeEnd <= fieldEnd) ||
          (fieldStart < rangeEnd && fieldEnd > rangeStart)
        );
      }
      return false; // if positioning, start, or length is not defined
    });
  }

  public getFormFieldByPosition(position: number) {
    var result = this.formFieldsService.allFields.find(
      (e) =>
        e.positioning &&
        e.positioning.start &&
        e.positioning.start <= position &&
        e.positioning.start + e.positioning.length! >= position
    );
    return cloneDeep(result);
  }

  public getFormFieldByTokenName(token: string) {
    var result = this.formFieldsService.allFields.find((e) => e.details.name === token);
    return result;
  }

  public async getUpdatedTokenWithReplacedText(
    textToFind: string,
    textToReplace: string,
    position?: number,
    caseSensitive: boolean = false
  ): Promise<FormField | undefined> {
    var obj: ObjectModel | undefined;
    if (position) {
      obj =
        this.getIfSelectionIsANavigatableQTokenField(position) ||
        this.getIfSelectionIsANavigatableITokenField(position);
      if (obj) {
        const result = await this.findTokenAndReplaceText(
          textToFind,
          textToReplace,
          position,
          caseSensitive,
          obj.hasTransformations
        );
        return result;
      }
    }
    return undefined;
  }

  private async findTokenAndReplaceText(
    textToFind: string,
    textToReplace: string,
    position: number,
    caseSensitive: boolean = false,
    hasTransformation: boolean = false
  ) {
    var result = this.getFormFieldByPosition(position);
    if (!result) {
      return;
    }
    if (this.getIfSelectionIsANavigatableITokenField(position)) {
      this.formFieldsService.activeSelectedField = this.getFormFieldByPosition(position);
      this.formFieldToDeleteFromEditDocumentComponent = this.formFieldsService.activeSelectedField?.details.name ?? '';
      await this.deleteCurrentFormFieldAsync();
      this.formFieldsService.activeSelectedField = undefined;
      this.formFieldsService.currentSelectedFieldId = -1;
      this.formFieldToDeleteFromEditDocumentComponent = '';
      const txContext = new TextControlContext();
      await txContext.selection.setStart(position);
      await txContext.selection.setLength(textToFind.length);
      return undefined;
    }
    result.details.text = this.returnReplaceText(
      result.details.text,
      textToFind,
      hasTransformation ? textToReplace.toLocaleUpperCase() : textToReplace,
      caseSensitive
    );

    return result;
  }

  public async replaceHighlightedText(textToReplace: string) {
    return new Promise((resolve) => {
      TXTextControl.selection.setText(textToReplace, () => {
        resolve(true);
      });
    });
  }

  private returnReplaceText(word: string, textToFind: string, textToReplace: string, caseSensitive: boolean = false) {
    let wordToReplace = word;
    let replacedWord: string = '';
    if (!caseSensitive) {
      let regex = new RegExp(textToFind, 'gi');
      replacedWord = wordToReplace.replace(regex, textToReplace);
      return replacedWord;
    }

    replacedWord = wordToReplace.replace(textToFind, textToReplace);

    return replacedWord;
  }

  public async replaceAllText(
    textToFind: string,
    textToReplace: string,
    txDocsSearchScope: TXdocsSearchScope,
    findOptionsBitMask: number
  ) {
    if (this.replaceAllCalled) {
      return;
    }
    try {
      this.replaceAllCalled = true;

      var result = await this.findNextInBody(textToFind, 0, findOptionsBitMask, txDocsSearchScope);

      while (result !== FindResult.NotFound) {
        if (!this.checkIfSelectionInFormField(result)) {
          this.replaceHighlightedText(textToReplace);
        }
        result = await this.findNextInBody(textToFind, result, findOptionsBitMask, txDocsSearchScope);
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.replaceAllCalled = false;
    }
  }

  private checkIfSelectionInFormField(position: number): boolean {
    var result = this.getFormFieldByPosition(position);
    return result ? true : false;
  }

  private getIfSelectionIsANavigatableQTokenField(position: number): ObjectModel | undefined {
    var result = this.getFormFieldByPosition(position);

    if (!result) {
      return undefined;
    }
    return this.getIfTokenIsANavigatableQTokenField(result.details.name);
  }

  private getIfTokenIsANavigatableQTokenField(token: string): ObjectModel | undefined {
    var obj = this.documentDataService.getObject(token);
    if (!obj) {
      return undefined;
    }
    if (obj.objType === 'Q' ? true : false) {
      //f (obj.objType === 'I' && obj.qToken && !obj.hasTransformations ? true : obj.objType === 'Q' ? true : false) {
      return obj;
    }
    return undefined;
  }

  private getIfSelectionIsANavigatableITokenField(position: number): ObjectModel | undefined {
    var result = this.getFormFieldByPosition(position);

    if (!result) {
      return undefined;
    }
    return this.getIfTokenIsANavigatableITokenField(result.details.name);
  }

  private getIfTokenIsANavigatableITokenField(token: string): ObjectModel | undefined {
    var obj = this.documentDataService.getObject(token);
    if (!obj) {
      return undefined;
    }
    if (obj.objType === 'I') {
      return obj;
    }
    return undefined;
  }

  public async findNextInBody(
    text: string,
    startPosition: number,
    options: number,
    txDocsSearchScope: TXdocsSearchScope
  ): Promise<number> {
    return new Promise(async (resolve) => {
      try {
        let itemFound = false;
        let result: number = startPosition;
        let actualPosition: number = startPosition;

        console.log(`FindInBody called. Start position: ${startPosition}.`);
        if (this.isInActiveFind) {
          console.log(`recusivly called?: ${startPosition}.`);
          return;
        }

        this.isInActiveFind = true;
        //To-Do: Add Loading Spinner

        this.disableHeaderFooter();

        let cloneOptions = options;
        //During iteration once item found form field gets selected for a second if highlight is enabled, we want to skip it.
        let findOptions = this.helperService.deconvertBitmask(options, FindOptions);
        if (!findOptions.includes(FindOptions.NoHightLight)) {
          cloneOptions += FindOptions.NoHightLight;
        }
        //Critical path, wait for the find to be done.
        while (!itemFound) {
          result = await this.TXTextControlFindInBody(text, actualPosition, cloneOptions);
          if (result === FindResult.NotFound) {
            break;
          }
          actualPosition = result;

          let fieldsInSelection = this.getFieldsInRange(result, text.length);
          let foundContent = false;
          let foundAnswer = false;
          let foundCalc = false;

          for (let currentPosition = result; currentPosition < result + text.length; currentPosition++) {
            const fieldAtPos = fieldsInSelection.find((field) => {
              if (field.positioning && field.positioning.start && field.positioning.length) {
                return (
                  field.positioning.start <= currentPosition &&
                  currentPosition < field.positioning.start + field.positioning.length
                );
              }
              return false;
            });
            if (fieldAtPos && fieldAtPos.positioning && fieldAtPos.positioning.start && fieldAtPos.positioning.length) {
              currentPosition = fieldAtPos.positioning.start + fieldAtPos.positioning.length;
              let obj = this.getIfTokenIsANavigatableQTokenField(fieldAtPos.details.name);
              if (obj) {
                foundAnswer = true;
              } else {
                obj = this.getIfTokenIsANavigatableITokenField(fieldAtPos.details.name);
                if (obj) {
                  foundCalc = true;
                }
              }
            } else {
              foundContent = true;
            }
          }
          if (
            (txDocsSearchScope.content == foundContent || !foundContent) &&
            (txDocsSearchScope.answerFields == foundAnswer || !foundAnswer) &&
            (txDocsSearchScope.calculatedFields == foundCalc || !foundCalc)
          ) {
            if (findOptions.includes(FindOptions.SearchUp)) {
              await this.TXTextControlFindInBody(text, result + text.length, options);
            } else {
              await this.TXTextControlFindInBody(text, result, options);
            }
            await this.focusAndScrollToPosition(result, text.length);
            itemFound = true;
          } else {
            actualPosition++;
          }
        }
        resolve(itemFound ? result : FindResult.NotFound);
      } catch (e) {
        console.error(e);
        resolve(FindResult.NotFound);
      } finally {
        this.isInActiveFind = false;
      }
    });
  }

  private async focusAndScrollToPosition(position: number, length: number = 0) {
    this.focus();
    await this.setInputPosition(position, length, true);
    await this.scrollToPosition(position);
  }

  public async findNextInHeaderOrFooter(text: string, startPosition: number, options: number) {
    var result = await this.TXTextControlFindInHeaderOrFooter(text, startPosition, options);
    if (result !== FindResult.NotFound) {
      this.enableHeaderFooter();
      this.focus();
    }
    return result;
  }

  public async TXTextControlFindInHeaderOrFooter(
    text: string,
    startPosition: number,
    options: number
  ): Promise<number> {
    return new Promise((resolve) => {
      if (!this.headerFooter) {
        resolve(FindResult.NotFound);
      }
      this.headerFooter.find(text, startPosition, options, (callback: number) => {
        resolve(callback);
      });
    });
  }

  public async TXTextControlFindInBody(text: string, startPosition: number, options: number): Promise<number> {
    return new Promise((resolve) => {
      TXTextControl.find(text, startPosition, options, (result: any) => {
        resolve(result);
      });
    });
  }

  public async enableHeaderFooter() {
    return new Promise(async (resolve) => {
      if (!this.headerFooter) {
        return;
      }
      this.headerFooter.activate(() => {
        resolve(true);
      });
    });
  }

  public async disableHeaderFooter() {
    return new Promise(async (resolve) => {
      if (!this.headerFooter) {
        return;
      }
      this.headerFooter.deactivate(() => {
        resolve(true);
      });
    });
  }

  public async scrollToPosition(position: number, insideField: boolean = false) {
    return new Promise(async (resolve) => {
      //TXTextControl.inputPosition.scrollTo(position, () => {
      TXTextControl.inputPosition.scrollTo(0, () => {
        resolve(true);
      });
    });
  }

  async initializeTextControl() {
    let lastStartPos = 0;
    let lastLength = 0;
    let lastFieldId = -1;
    TXTextControl.isLoadingDialogEnabled = true;
    this._zoom$.next(TXTextControl.zoomFactor);
    this._locationEvent$ = fromEvent(TXTextControl, 'textViewLocationChanged', { capture: false });
    this._zoomEvent$ = fromEvent(TXTextControl, 'zoomFactorChanged', { capture: false });
    this.subscriptions.add(
      this._zoomEvent$
        .pipe(
          tap((e) => {
            this._zoom$.next(e.zoomFactor);
          })
        )
        .subscribe()
    );
    this.subscriptions.add(
      this._locationEvent$
        .pipe(
          tap((e) => {
            this._location$.next(e.location);
            //this.location = e.location;
          })
        )
        .subscribe()
    );
    this.subscriptions.add(
      combineLatest([
        //this.watchTextViewLocationChanged(),
        this.watchFieldArrayLocationsActiveFieldObservable(),
        //this.formFieldsService.watchFieldArrayLocationsObservable(),
      ])
        .pipe(
          map(
            ([
              //viewPos,
              activeFields,
              //allFields
            ]) => {
              // fires after field
              // console.debug(
              //   activeFields?.selectedField?.details?.name ?? 'no field',
              //   activeFields?.selectedField?.details?.id ?? 'no field',
              //   activeFields?.selectionPos.selection.start,
              //   activeFields?.selectionPos.selection.length
              // );
              if (
                lastFieldId !== activeFields.selectedField?.details?.id ||
                lastStartPos !== activeFields.selectionPos.selection.start ||
                lastLength !== activeFields.selectionPos.selection.length
              ) {
                lastFieldId = activeFields.selectedField?.details?.id;
                lastStartPos = activeFields.selectionPos.selection.start;
                lastLength = activeFields.selectionPos.selection.length;

                this.formFieldsService._activeField$.next(activeFields.selectedField);
              }

              return {
                selectionPos: activeFields.selectionPos.selection as SelectionPosition,
                currentField: activeFields.selectedField as FormField,
              };
            }
          )
        )
        // redrawing issues / no way to clear the canvas or redraw small
        .subscribe((results) => {
          // this.allFields = results.formFields;
          // this.currentSelectedFieldId =
          //   results.currentField && results.currentField.details ? results.currentField.details.id : -1;
          // this.activeSelectedField = results.currentField;
        })
    );

    // THIS CODE BELOW IS FOR TRACKING UNDO AND FIELD EDITS THAT CAN CAUSE PROBLEMS
    // this.subscriptions.add(
    //   this.watchSelectedFieldArrayLocationsObservable().subscribe((selectedFields) => {
    //     console.log('Selected Fields', selectedFields.length);
    //     this.selectedFields = selectedFields;
    //   })
    // );
    this.subscriptions.add(this.watchClipboardDataTransferStartObservable().subscribe());
    this.subscriptions.add(this.watchClipboardDataTransferCompleteObservable().subscribe());
    this.subscriptions.add(this.watchClipboardDataTransferAbortedObservable().subscribe());
    this.subscriptions.add(this.watchClipboardDataTransferProgressObservable().subscribe());

    this.listenToEventsAndLog();
    this.formFieldsService.onStart();
    await this.loadHeaderFooter();
  }

  undo(callback: any) {
    TXTextControl.undo((x: any) => callback());
  }

  focus() {
    TXTextControl.focus();
  }

  undoAndFocus() {
    TXTextControl.undo(() => {
      this.focus();
    });
  }

  getInputPosition(): Promise<number> {
    return new Promise((resolve) => {
      TXTextControl.inputPosition.getTextPosition((position: number) => resolve(position));
    });
  }

  public async deleteCurrentFormFieldAsync() {
    const txContext = new TextControlContext();
    const position = (await txContext.selection.getStart()) - 1;
    const currentField = await txContext.formFields.getItem();
    await this.callAsPromise(TXTextControl.beginUndoAction, 'DeleteField');
    const id = await currentField.getId();
    const text = await currentField.getText();
    const ffCollection = new FormFieldCollection(TXTextControl.formFields);
    await ffCollection.remove(currentField);
    this.refreshFormFieldsArray([id]);
    await txContext.selection.setText(text);
    await txContext.selection.setStart(position);
    await this.callAsPromiseNoParams(TXTextControl.endUndoAction);
  }

  public async deleteFormFieldAsync(formField: ffPromise) {
    const txContext = new TextControlContext();
    const ffCollection = new FormFieldCollection(TXTextControl.formFields);
    const position = (await formField.getStart()) - 1;
    const text = await formField.getText();
    await this.callAsPromise(TXTextControl.beginUndoAction, 'DeleteField')
      .then((r) => ffCollection.remove(formField))
      .then((r) => txContext.selection.setStart(position))
      .then((r) => txContext.selection.setLength(0))
      .then((r) => txContext.selection.setText(text))
      .then((r) => this.callAsPromiseNoParams(TXTextControl.endUndoAction));
  }

  public deleteCurrentFormField() {
    //nothing prevents navigation or .focus events so we could potentially delete a field after the command is issued
    if (this.formFieldToDeleteFromEditDocumentComponent === this.formFieldsService.activeSelectedField?.details.name) {
      this.getInputPosition().then((position: number) => {
        TXTextControl.formFields.getItem((ff: any) => {
          TXTextControl.beginUndoAction('DeleteField', () => {
            ff.getID((id: number) => {
              let text = '';
              ff.getText((t: string) => {
                text = t;
                TXTextControl.formFields.remove(ff, (val: boolean) => {
                  this.refreshFormFieldsArray([id]);
                  if (val === true) {
                    TXTextControl.selection.setText(text, () => {
                      TXTextControl.setInputPositionByTextPosition(
                        position,
                        TXTextControl.TextFieldPosition.OutsideTextField,
                        () => this.focus()
                      );
                    });
                  }
                  TXTextControl.endUndoAction();
                });
              });
            });
          });
        });
      });
    } else {
      this.undoAndFocus();
      console.error(`Fields have changed since delete command was issued`);
    }
  }

  public refreshFormFieldsArray(removeIds: number[]) {
    // await this.formFieldsService.getAllFormFields();
    removeIds.forEach((id: number) => {
      const idx = this.formFieldsService.allFields.findIndex((ff: FormField) => ff.details.id === id);
      if (idx > -1) this.formFieldsService.allFields.splice(idx, 1);
    });

    this.formFieldsService.getTextPartsCount().then((tpCount: number) => {
      this.formFieldsService.allFields.forEach((ff: FormField) => {
        TXTextControl.formFields.getItem(
          async (e: any) => {
            ff.element = e;
            await this.formFieldsService.getFormFieldReferencePromise(ff, tpCount);
          },
          (err: any) => {
            console.log(err);
          },
          ff.details.id
        );
      });
    });
  }

  setInputPosition(position: number, length: number = 0, insideTextField: boolean): Promise<boolean> {
    const textFieldPosition = insideTextField
      ? TXTextControl.TextFieldPosition.InsideTextField
      : TXTextControl.TextFieldPosition.OutsideTextField;
    return new Promise((resolve) => {
      TXTextControl.setInputPositionByTextPosition(position, textFieldPosition, () => resolve(true));
      if (length > 0) {
        TXTextControl.selection.setLength(length);
      }
    });
  }

  public getDocumentUnderscoreAnswers() {
    TXTextControl.documentSettings.getUserDefinedDocumentProperties((uddp: any) => {
      if (uddp && uddp[this.underscoreAnswersKey]) {
        this.underscoreAnswers = JSON.parse(uddp[this.underscoreAnswersKey]);
      }
    });
  }

  public clearActiveField() {
    this.formFieldsService.activeSelectedField = undefined;
    this.formFieldsService.currentSelectedFieldId = -1;
  }

  public clearAllFields() {
    this.formFieldsService.allFields = [];
  }

  public callAsPromiseNoParams(request: Function): Promise<any> {
    return RequestHelper.Promise(request, CallbackType.EmptyRequestCallback, CallbackType.ErrorCallback);
  }

  public callAsPromise(request: Function, param: any): Promise<any> {
    return RequestHelper.Promise(request, param, CallbackType.EmptyRequestCallback, CallbackType.ErrorCallback);
  }
  public getActiveField(): Promise<any | undefined> {
    return new Promise((resolve) => {
      TXTextControl.formFields.getItem((current: any) => {
        resolve(current);
      });
    });
  }
  public getFieldById(Id: Number): Promise<any | undefined> {
    return new Promise((resolve, reject) => {
      TXTextControl.formFields.getItem(
        (current: any) => {
          resolve(current);
        },
        (error: any) => {
          reject(error);
        },
        Id
      );
    });
  }

  async getParagraphCountPromise(): Promise<number> {
    return new Promise((resolve, reject) => {
      TXTextControl.paragraphs.getCount(
        (count: number) => {
          resolve(count);
        },
        (error: any) => {
          reject(error);
        }
      );
    });
  }

  async getParagraphByIndexPromise(index: number): Promise<any> {
    return new Promise((resolve, reject) => {
      TXTextControl.paragraphs.elementAt(
        index,
        (paragraph: any) => {
          resolve(paragraph);
        },
        (error: any) => {
          reject(error);
        }
      );
    });
  }

  async getParagraphAtPositionPromise(position: number): Promise<any> {
    return new Promise((resolve, reject) => {
      TXTextControl.paragraphs.getItemAtTextPosition(position, (paragraph: any) => {
        resolve(paragraph);
      });
    });
  }
  async getParagraphStartPositionPromise(paragraph: any): Promise<number> {
    return new Promise((resolve, reject) => {
      paragraph.getStart((startPosition: number) => {
        // documentation says this is 1 based (SMH)
        resolve(startPosition - 1);
      });
    });
  }
  async getParagraphLengthPromise(paragraph: any): Promise<number> {
    return new Promise((resolve, reject) => {
      paragraph.getLength((length: number) => {
        // documentation says this includes end character (SMH)
        resolve(length);
      });
    });
  }

  async getParagraphListNumber(paragraph: any): Promise<number> {
    return new Promise((resolve, reject) => {
      paragraph.getListNumber(
        (listNumber: number) => {
          resolve(listNumber);
        },
        (error: any) => {
          reject(error);
        }
      );
    });
  }

  async getParagraphListFormatRestartNumbering(paragraph: any): Promise<boolean> {
    return new Promise((resolve, reject) => {
      paragraph.listFormat.getRestartNumbering(
        (restart: boolean) => {
          resolve(restart);
        },
        (error: any) => {
          reject(error);
        }
      );
    });
  }

  async setParagraphListFormatRestartNumbering(paragraph: any, restart: boolean): Promise<void> {
    return new Promise((resolve, reject) => {
      paragraph.listFormat.setRestartNumbering(
        restart,
        () => {
          resolve();
        },
        (error: any) => {
          reject(error);
        }
      );
    });
  }

  getTextPartLengthAsPromise() {
    return new Promise<number>((resolve, reject) => {
      TXTextControl.textParts.getItem(
        (tp: any) => {
          tp.textChars.getCount(
            (tc_count: any) => {
              this.currentTextPartLength.set(tc_count);
              resolve(tc_count);
            },
            (err: any) => {
              reject(err);
            }
          );
        },
        (err: any) => {
          reject(err);
        }
      );
    });
  }

  async getCharacterAtLocationAsPromise(locationPosition: number): Promise<string> {
    let txContext = new TextControlContext();
    let charElementAtLocation = await txContext.textChars.elementAt(locationPosition);
    let textCharAtLocation = new TextChar(charElementAtLocation);
    let charAtElement = await textCharAtLocation.getChar();
    return charAtElement;
  }

  getCurrentStyleAsPromise() {
    return new Promise<string>((resolve, reject) => {
      TXTextControl.selection.getFormattingStyle(
        (style: string) => {
          resolve(style);
        },
        (err: any) => {
          reject(err);
        }
      );
    });
  }

  setCurrentStyleAsPromise(newStyle: string) {
    return new Promise<void>((resolve, reject) => {
      TXTextControl.selection.setFormattingStyle(
        newStyle,
        () => {
          resolve();
        },
        (err: any) => {
          reject(err);
        }
      );
    });
  }

  rebuildCaseDictionary() {
    TXTextControl.Proofing.removeUserDictionary('CaseDictionary');
    this.addCaseDictionary();
  }

  addCaseDictionary() {
    const autocompleteAnswers = this.documentDataService
      .getAnswers()
      .filter((a) => this.documentDataService.getObject(a.token)?.autoComplete)
      .map((a) => a.answer);
    let uniqueWords = this.dictionaryService.getUniqueWordsForCaseDictionary(autocompleteAnswers);
    this.dictionaryService
      .getFirmNameAndStaffForCaseDictionary()
      .pipe(take(1))
      .subscribe({
        next: (res) => {
          if (res && res.words) {
            const uniqueFirmWords = this.dictionaryService.getUniqueWordsForCaseDictionary(res.words);
            uniqueWords = uniqueWords.concat(uniqueFirmWords);
            this.addDictionary('CaseDictionary', uniqueWords);
          }
        },
        error: (err) => {
          console.log(err);
          this.addDictionary('CaseDictionary', uniqueWords);
        },
      });
  }

  addDictionary(name: string, words: string[]) {
    const attemptLoad = () => {
      TXTextControl.getIsSpellCheckingEnabled((enabled: boolean) => {
        if (enabled) {
          // check if the default dictionary is loaded
          // if we load a user dictionary before that, then the default on won't be loaded
          TXTextControl.Proofing.getDictionaryList((l: any[]) => {
            if (l.length > 0 && l[0] != null && l[0].isLoaded === true) {
              TXTextControl.Proofing.loadUserDictionary(name, words, 'en-US');
              // console.log('dictionary loaded: ', name);
            } else {
              // console.log('default dictionary not loaded');
              setTimeout(attemptLoad, 1000);
            }
          });
        } else {
          // console.log('spell checking not enabled');
          setTimeout(attemptLoad, 1000);
        }
      });
    };

    attemptLoad();
  }

  getFirmDictionary(): Promise<string[]> {
    return new Promise((resolve) => {
      TXTextControl.Proofing.getUserDictionaryInfo((i: any[]) => {
        if (i && i.length > 0) {
          TXTextControl.Proofing.saveUserDictionary('FirmDictionary', (name: string, words: string[]) => {
            resolve(words);
          });
        } else {
          resolve([]);
        }
      });
    });
  }

  insertLetterhead(letterhead: LetterheadModel): Promise<boolean> {
    return new Promise((resolve) => {
      if (letterhead && letterhead.file) {
        switch (letterhead.type) {
          case LetterheadType.ImageFile:
            TXTextControl.beginUndoAction('InsertImageLetterhead', () => {
              this.insertImageLetterhead(letterhead, () => {
                TXTextControl.endUndoAction(() => {
                  this.focusAndScrollToPosition(0);
                  resolve(true);
                });
              });
            });
            break;
          case LetterheadType.TextFile:
            TXTextControl.beginUndoAction('InsertTextLetterhead', () => {
              TXTextControl.setInputPositionByTextPosition(0, TXTextControl.TextFieldPosition.OutsideTextField, () => {
                TXTextControl.selection.load(TXTextControl.StreamType.RichTextFormat, letterhead.file, () => {
                  TXTextControl.selection.setText('\n', () => {
                    TXTextControl.endUndoAction(() => {
                      this.focusAndScrollToPosition(0);
                      resolve(true);
                    });
                  });
                });
              });
            });
            break;
          default:
            resolve(false);
            break;
        }
      } else {
        resolve(false);
      }
    });
  }

  private insertImageLetterhead(letterhead: LetterheadModel, callback: (image: any) => void) {
    TXTextControl.images.addAnchored(letterhead.file, TXTextControl.HorizontalAlignment.Center, 0, 0, (img: any) => {
      img.getSize((size: any) => {
        img.setHorizontalScaling(recalcImageRatioFromTwips(size.width, letterhead.width / 1000), () => {
          img.setVerticalScaling(recalcImageRatioFromTwips(size.height, letterhead.height / 1000), () => {
            TXTextControl.selection.setText('\n', () => {
              callback(img);
            });
          });
        });
      });
    });
  }

  public async copyCurrentSelectionText(): Promise<{ html: string; plaintext: string }> {
    return new Promise(async (resolve) => {
      const selection = await this.getSelectionInformationPromise();
      const fieldsInSelection = this.getFieldsInRange(selection.start, selection.length);
      let emptyFields: FormField[] = [];
      let savedSelection: string = '';
      let savedPlaintextSelection: string = '';
      await this.callAsPromise(TXTextControl.beginUndoAction, 'Copy');

      try {
        if (fieldsInSelection && fieldsInSelection.length > 0) {
          await this.callAsPromise(TXTextControl.beginUndoAction, 'ReplaceEmptyValues');
          emptyFields = await this.replaceEmptyFieldValues(fieldsInSelection);
        }

        savedSelection = await this.saveSelection(TXTextControl.StreamType.HTMLFormat);
        savedPlaintextSelection = await this.saveSelection(TXTextControl.StreamType.PlainText);

        if (fieldsInSelection && fieldsInSelection.length > 0) {
          await this.callAsPromiseNoParams(TXTextControl.endUndoAction); // 'ReplaceEmptyValues'
          await this.revertReplacingEmptyValues(emptyFields);
        }
      } catch (error) {
        throw error;
      } finally {
        await this.setInputPosition(selection.start, selection.length, false);
        this.focus();
        await this.callAsPromiseNoParams(TXTextControl.endUndoAction); // 'Copy'
      }

      resolve({ html: savedSelection, plaintext: savedPlaintextSelection });
    });
  }

  public async cutCurrentSelectionText(): Promise<{ html: string; plaintext: string }> {
    return new Promise(async (resolve) => {
      const selection = await this.getSelectionInformationPromise();
      const fieldsInSelection = this.getFieldsInRange(selection.start, selection.length);
      let emptyFields: FormField[] = [];
      let savedSelection: string = '';
      let savedPlaintextSelection: string = '';
      await this.callAsPromise(TXTextControl.beginUndoAction, 'Cut');
      try {
        if (fieldsInSelection && fieldsInSelection.length > 0) {
          await this.callAsPromise(TXTextControl.beginUndoAction, 'ReplaceEmptyValues');
          emptyFields = await this.replaceEmptyFieldValues(fieldsInSelection);
        }
        savedSelection = await this.saveSelection(TXTextControl.StreamType.HTMLFormat);
        savedPlaintextSelection = await this.saveSelection(TXTextControl.StreamType.PlainText);
        if (fieldsInSelection && fieldsInSelection.length > 0) {
          await this.callAsPromiseNoParams(TXTextControl.endUndoAction); // 'ReplaceEmptyValues'
          await this.revertReplacingEmptyValues(emptyFields);
        }
        await this.setInputPosition(selection.start, selection.length, true);
        await this.replaceHighlightedText('');
        const cutFieldsIds = fieldsInSelection.map((ff) => ff.details.id);
        // Update form fields array after fields are cut out.
        // Not doing this results in console errors when trying to access fields whose position is after the cut text
        this.refreshFormFieldsArray(cutFieldsIds);
      } catch (error) {
        throw error;
      } finally {
        this.focus();
        await this.callAsPromiseNoParams(TXTextControl.endUndoAction); // 'Cut'
      }

      resolve({ html: savedSelection, plaintext: savedPlaintextSelection });
    });
  }

  public getCurrentFrame(): Promise<any> {
    return new Promise((resolve) => {
      TXTextControl.frames.getItem((frame: any) => {
        resolve(frame);
      });
    });
  }

  private async replaceEmptyFieldValues(fields: FormField[]): Promise<FormField[]> {
    this._isPreFormattingForPrint = true;
    const emptyFields = fields.filter((f) => f.details.text == null || f.details.text === '');
    const setFieldTextPromises = emptyFields.map((f) => {
      const obj = this.documentDataService.getObject(f.details.name);
      const logicResult = this.documentDataService.getLogicResultByToken(f.details.name);
      const isQtoken = obj && obj.token.startsWith('Q');
      const isCalculatedField = obj && !obj.token.startsWith('Q') && !obj.qToken;
      const hasAction = logicResult && logicResult.actions && logicResult.actions.length > 0.
      let replaceWith: string = (!isCalculatedField && hasAction) || isQtoken ? this.emptyFieldPlaceholder : '';
      const underscoreAnswer = this.underscoreAnswers[f.details.name];
      if (underscoreAnswer) {
        replaceWith = underscoreAnswer;
      }
      return this.formFieldsService.setFieldTextPromise(f, replaceWith, true);
    });
    await Promise.all(setFieldTextPromises);
    return emptyFields;
  }

  private async revertReplacingEmptyValues(emptyFields: FormField[]) {
    if (emptyFields && emptyFields.length > 0) {
      await this.callAsPromiseNoParams(TXTextControl.undo);
      // TXTextControl.clearUndo();
    }
    this._isPreFormattingForPrint = false;
  }

  private saveSelection(format: any): Promise<string> {
    return new Promise((resolve) => {
      TXTextControl.selection.save(format, (savedSelection: any) => {
        resolve(savedSelection.data);
      });
    });
  }

  public loadIntoSelection(content: string, streamType: any, options: any) {
    return new Promise((resolve) => {
      TXTextControl.selection.load(
        streamType,
        content,
        () => {
          resolve(true);
        },
        options
      );
    });
  }

  public getSelectionFontName(): Promise<string> {
    return new Promise((resolve) => {
      TXTextControl.selection.getFontName((name: string) => {
        resolve(name);
      });
    });
  }

  public getSelectionFontSize(): Promise<number> {
    return new Promise((resolve) => {
      TXTextControl.selection.getFontSize((size: number) => {
        resolve(size);
      });
    });
  }

  public getPageInformationPromise(): Promise<PageInformation> {
    return Promise.all([
      new Promise<number>((resolve) => TXTextControl.pageSize.getHeight((height: number) => resolve(height))),
      new Promise<number>((resolve) => TXTextControl.pageSize.getWidth((width: number) => resolve(width))),
      new Promise<number>((resolve) => TXTextControl.pageMargins.getTop((top: number) => resolve(top))),
      new Promise<number>((resolve) => TXTextControl.pageMargins.getRight((right: number) => resolve(right))),
      new Promise<number>((resolve) => TXTextControl.pageMargins.getBottom((bottom: number) => resolve(bottom))),
      new Promise<number>((resolve) => TXTextControl.pageMargins.getLeft((left: number) => resolve(left))),
    ]).then(([height, width, top, right, bottom, left]) => ({
      pageHeight: height,
      pageWidth: width,
      marginTop: top,
      marginRight: right,
      marginBottom: bottom,
      marginLeft: left,
    }));
  }

  async setContinueNumberingOnCurrentParagraph() {
    const inputPosition = await this.getInputPosition();
    const currentPar = await this.getParagraphAtPositionPromise(inputPosition);
    const currentParStart = await this.getParagraphStartPositionPromise(currentPar);
    const currentParRestartNumbering = await this.getParagraphListFormatRestartNumbering(currentPar);
    const prevPar = await this.getParagraphAtPositionPromise(currentParStart - 1);
    const prevParRestartNumbering = await this.getParagraphListFormatRestartNumbering(prevPar);
    const prevParListNumber = await this.getParagraphListNumber(prevPar);

    if (prevParRestartNumbering === true && prevParListNumber === 1 && currentParRestartNumbering === true) {
      await this.setParagraphListFormatRestartNumbering(currentPar, false);
    }
  }
  async insertSmartDoubleQuotes() {
    let existingCharBefore = '';
    const startPosition = this.currentSelection().start;
    const textPartLength = this.currentTextPartLength();
    const isNotEmpty = textPartLength !== 0;
    const isAtStart = startPosition === 0;

    try {
      if (!isNotEmpty || isAtStart) {
        TXTextControl.selection.setText('“');
      } else {
        if (!isAtStart) {
          existingCharBefore = await this.getCharacterAtLocationAsPromise(startPosition - 1);
        }
        if (
          existingCharBefore == ' ' ||
          existingCharBefore == '' ||
          existingCharBefore == '\v' || //Softreturn
          existingCharBefore == '\f' || //Formfeed
          existingCharBefore == '\t' ||
          existingCharBefore == '\n' ||
          existingCharBefore == '\r' ||
          existingCharBefore == '(' ||
          existingCharBefore == '[' ||
          existingCharBefore == '{'
        ) {
          TXTextControl.selection.setText('“');
        } else {
          TXTextControl.selection.setText('”');
        }
      }
    } catch (error) {
      console.error('Error fetching characters at location:', error);
      return;
    }
  }
}
